import { useCallback, useState } from "react";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { initialServicosProdutosListaFilters, useServicosProdutosListaFilters } from "../hooks/ServicosProdutos_lista/filters";
import { useServicosProdutosListaPagination } from "../hooks/ServicosProdutos_lista/pagination";
import { ServicoProdutoLista } from "../../domain/models/servico_produto_lista";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { processError } from "../../../../common/utils/base_error";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { ServicoProdutoForm } from "../schemas/ServicosProdutos_lista/servico_produto";
import { GridRowSelectionModel } from "@mui/x-data-grid-pro";
import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";

/**
 * LoadServicosProdutosFuncState
 */

type LoadServicosProdutosFuncState = {
  field?: number;
  fieldValue?: string;
  tipo?: number;
  status?: number;
  page?: number;
  rowsPerPage?: number;
};

/**
 * useServicosProdutosListaViewModel
 */

export const useServicosProdutosListaViewModel = () => {

  const {
    compras: {
      getAllServicoProdutoUseCase,
      createServicoProdutoUseCase,
      updateServicoProdutoUseCase,
      deleteServicoProdutoUseCase,
      inativarServicoProdutoLoteServicoProdutoUseCase,
    },
    reports: {
      getReportServicoProdutoUseCase,
    },
    commons: {
      getAllLogAlteracaoUseCase,
      getAllLogAlteracaoByParentUseCase,
    }
  } = useDependencyInjection();

  const { pushInfoNotification, pushErrorNotification, setPageSaving } = usePageLoader();

  const debouncedHandler = useCreateDebounceHandler(750);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [servicosProdutosModal, setServicosProdutosModal] = useState(false);
  const [servicosProdutosDeleteModal, setServicosProdutosDeleteModal] = useState(false);

  const [selectedProdutoServico, setSelectedProdutoServico] = useState<ServicoProdutoLista>();

  const [produtosServicos, setProdutosServicos] = useState<ServicoProdutoLista[]>();

  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>({ type: 'include', ids: new Set() });

  const [reportViewerState, startReportViewerProcess] = useReportViewer();

  const filtros = useServicosProdutosListaFilters();
  const pagination = useServicosProdutosListaPagination();

  const changeHistory = useChangeHistory({
    getAllLogAlteracaoUseCase,
    getAllLogAlteracaoByParentUseCase,
    onSetError: message => pushErrorNotification('Erro', message),
  });

  /**
   * Funções de carregamento
   */

  const loadServicosProdutos = useCallback(async (fs?: LoadServicosProdutosFuncState) => {
    try {
      const filteredField = evaluateParamValue(fs?.field, filtros.field);
      const filteredFieldValue = evaluateParamValue(fs?.fieldValue, filtros.fieldValue);
      const filteredTipo = evaluateParamValue(fs?.tipo, filtros.tipo);
      const filteredStatus = evaluateParamValue(fs?.status, filtros.status);

      const filteredPage = fs?.page ?? pagination.page;
      const filteredRowsPerPage = fs?.rowsPerPage ?? pagination.rowsPerPage;

      await getAllServicoProdutoUseCase.getAllServicoProduto(
        filteredField,
        filteredFieldValue,
        filteredTipo,
        filteredStatus,
        filteredPage,
        filteredRowsPerPage,
      );

      setProdutosServicos(getAllServicoProdutoUseCase.items);

      pagination.setTotalItems(getAllServicoProdutoUseCase.totalItems);
    } catch (e: unknown) {
      setProdutosServicos(undefined);
      throw new Error('Falha ao buscar dados da página!');
    }
  }, [
    filtros,
    pagination,
    getAllServicoProdutoUseCase,
  ]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      await loadServicosProdutos();
    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Falha ao buscar dados da pagina!')[0]);
    }
  });

  /**
   * Eventos
   */

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);

      await loadServicosProdutos();

    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadServicosProdutos,
    pushErrorNotification,
  ]);

  const handleServicosProdutosSubmit = useCallback(async (form: ServicoProdutoForm) => {
    try {
      setSaving(true);
      setPageSaving(true);

      if (selectedProdutoServico) {
        await updateServicoProdutoUseCase.updateServicoProduto(
          selectedProdutoServico.idProdutoServico,
          form.ativo,
          form.produtoServico,
          form.tipo,
          form.unidade,
          form.norma,
        );
      } else {
        await createServicoProdutoUseCase.createServicoProduto(
          form.ativo,
          form.produtoServico,
          form.tipo,
          form.unidade,
          form.norma,
        );
      }
      await loadServicosProdutos();
      setServicosProdutosModal(false);

      pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setSaving(false);
      setPageSaving(false);
    }
  }, [
    selectedProdutoServico,
    createServicoProdutoUseCase,
    updateServicoProdutoUseCase,
    loadServicosProdutos,
    pushErrorNotification,
    pushInfoNotification,
    setPageSaving,
  ]);

  const handleServicoProdutoDeleteSubmit = useCallback(async () => {
    try {
      setSaving(true);
      setPageSaving(true);

      if (selectedProdutoServico) {
        await deleteServicoProdutoUseCase.deleteServicoProduto(
          selectedProdutoServico.idProdutoServico,
        );
      }
      await loadServicosProdutos();
      setServicosProdutosDeleteModal(false);

      pushInfoNotification('Sucesso', 'Registro excluido com sucesso!');
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setSaving(false);
      setPageSaving(false);
    }
  }, [
    selectedProdutoServico,
    deleteServicoProdutoUseCase,
    loadServicosProdutos,
    pushErrorNotification,
    pushInfoNotification,
    setPageSaving,
  ]);

  const handleInativarSelecionadosSubmit = useCallback(async () => {
    try {
      setSaving(true);
      setPageSaving(true);

      if (rowSelectionModel && rowSelectionModel.ids.size > 0) {
        await inativarServicoProdutoLoteServicoProdutoUseCase
          .inativarServicoProdutoLoteServicoProduto(rowSelectionModel.ids);
        await loadServicosProdutos();
        pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');
      }
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setSaving(false);
      setPageSaving(false);
    }
  }, [
    rowSelectionModel,
    inativarServicoProdutoLoteServicoProdutoUseCase,
    loadServicosProdutos,
    pushErrorNotification,
    pushInfoNotification,
    setPageSaving,
  ]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      useServicosProdutosListaPagination.setState({ page: page + 1 });
      await loadServicosProdutos({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadServicosProdutos,
    pushErrorNotification,
  ]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      useServicosProdutosListaPagination.setState({ page: 1, rowsPerPage });
      await loadServicosProdutos({ rowsPerPage, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadServicosProdutos,
    pushErrorNotification,
  ]);

  const handleFieldChange = useDebouncedCallback(debouncedHandler, async (field: number) => {
    useServicosProdutosListaFilters.setState({ field });
    return async () => {
      try {
        setLoading(true);
        useServicosProdutosListaPagination.setState({ page: 1 });
        await loadServicosProdutos({ field, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadServicosProdutos,
    pushErrorNotification,
  ]);

  const handleFieldValueChange = useDebouncedCallback(debouncedHandler, (fieldValue: string) => {
    useServicosProdutosListaFilters.setState({ fieldValue });
    return async () => {
      try {
        setLoading(true);
        useServicosProdutosListaPagination.setState({ page: 1 });
        await loadServicosProdutos({ fieldValue, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadServicosProdutos,
    pushErrorNotification,
  ]);

  const handleTipoChange = useDebouncedCallback(debouncedHandler, (tipo: number) => {
    useServicosProdutosListaFilters.setState({ tipo });
    return async () => {
      try {
        setLoading(true);
        useServicosProdutosListaPagination.setState({ page: 1 });
        await loadServicosProdutos({ tipo, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadServicosProdutos,
    pushErrorNotification,
  ]);

  const handleStatusChange = useDebouncedCallback(debouncedHandler, (status: number) => {
    useServicosProdutosListaFilters.setState({ status });
    return async () => {
      try {
        setLoading(true);
        useServicosProdutosListaPagination.setState({ page: 1 });
        await loadServicosProdutos({ status, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadServicosProdutos,
    pushErrorNotification,
  ]);

  const handleClearFilters = useCallback(async () => {
    try {
      setLoading(true);

      useServicosProdutosListaFilters.setState({ ...initialServicosProdutosListaFilters });
      useServicosProdutosListaPagination.setState({ page: 1 });

      await loadServicosProdutos({ ...initialServicosProdutosListaFilters, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadServicosProdutos,
    pushErrorNotification,
  ]);

  const handleServicosProdutosModalOpenState = useCallback((idProdutoServico: number | null, openState: boolean) => {
    if (openState) {
      if (idProdutoServico && produtosServicos) {
        setSelectedProdutoServico(produtosServicos.find(x => x.idProdutoServico === idProdutoServico));
      } else {
        setSelectedProdutoServico(undefined);
      }
    }
    setServicosProdutosModal(openState);
  }, [
    produtosServicos,
  ]);

  const handleServicoProdutoDeleteModalOpenState = useCallback((idProdutoServico: number | null, openState: boolean) => {
    if (openState) {
      if (idProdutoServico && produtosServicos) {
        setSelectedProdutoServico(produtosServicos.find(x => x.idProdutoServico === idProdutoServico));
      } else {
        setSelectedProdutoServico(undefined);
      }
    }
    setServicosProdutosDeleteModal(openState);
  }, [
    produtosServicos,
  ]);

  const handleRowSelectionModel = useCallback((model: GridRowSelectionModel) => {
    setRowSelectionModel(model);
  }, [
  ]);

  const handleServicoProdutosReports = useCallback(async () => {
    try {
      setLoading(true);

      await startReportViewerProcess({
        fetchCallback: async (mimeType) =>
          await getReportServicoProdutoUseCase.getReportServicoProduto(
            mimeType,
          ),
      });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    getReportServicoProdutoUseCase,
    startReportViewerProcess,
    pushErrorNotification,
  ]);

  const handleOpenChangeHistory = useCallback(() => {
    if (selectedProdutoServico) {
      changeHistory.handleOpenChangeHistory(selectedProdutoServico.idProdutoServico, LogTipoTabela.PRODUTO_SERVICO, true);
    }
  }, [
    selectedProdutoServico,
    changeHistory,
  ]);

  return {
    loading,
    saving,
    errorMessage,
    servicosProdutosModal,
    servicosProdutosDeleteModal,
    selectedProdutoServico: selectedProdutoServico ?? null,
    produtosServicos: produtosServicos ?? [],
    rowSelectionModel,
    reportViewerState,
    filtros,
    pagination,
    changeHistory,
    handleReload,
    handleServicosProdutosSubmit,
    handleServicoProdutoDeleteSubmit,
    handleInativarSelecionadosSubmit,
    handlePageChange,
    handleRowsPerPageChange,
    handleFieldChange,
    handleFieldValueChange,
    handleTipoChange,
    handleStatusChange,
    handleClearFilters,
    handleServicosProdutosModalOpenState,
    handleServicoProdutoDeleteModalOpenState,
    handleRowSelectionModel,
    handleServicoProdutosReports,
    handleOpenChangeHistory,
  };
}